// components/ui/ConsumptionTimeline.tsx
'use client';

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import axios from 'axios';
import { Download } from '@/components/ui/lucide-memo';
import { ChartErrorBoundary } from './ChartErrorBoundary';
import SimpleLoader from './SimpleLoader';
import { formatCreditsInteger } from '#lib/formatCredits';
import { buildCsvContent } from '#lib/portal/csvExport';

// ChatGPT's usage chart (owner, 2026-09-29: 'rethink it from first principles'): one neutral
// series, the day's total. Who spent it is the ranked list under the chart, not a rainbow of
// stacked colours nobody can match to a name.
const BAR = '#8E8E8E';
const BAR_HOVER = '#ECECEC';
const CHART_THEME = {
  axis: { ticks: { text: { fill: '#8E8E8E', fontSize: 12 } } },
  grid: { line: { stroke: '#1F1F1F', strokeWidth: 1 } },
  tooltip: { container: { background: 'transparent', boxShadow: 'none', padding: 0 } },
};
const MEMBERS_SHOWN = 8;
const PERIOD_LABEL: Record<number, string> = { 7: 'in the last 7 days', 30: 'in the last 30 days', 90: 'in the last 90 days', 0: 'since the start' };

interface ConsumptionData {
  date: string;
  [userName: string]: string | number;
}

interface ConsumptionTimelineProps {
  /** Analytics endpoint, with or without an existing query string. `days` is appended by this component. */
  endpoint: string;
  /** Scope name, used to build the CSV file name. */
  scopeName: string;
  /** CSV file name prefix, before the sanitized scope name. */
  csvPrefix: string;
  /** Copy shown when the period holds no data. */
  emptyLabel: string;
  /** console.error prefix used when the fetch fails. */
  fetchErrorLabel: string;
}

const ConsumptionTimeline: React.FC<ConsumptionTimelineProps> = ({
  endpoint,
  scopeName,
  csvPrefix,
  emptyLabel,
  fetchErrorLabel,
}) => {
  const [data, setData] = useState<ConsumptionData[]>([]);
  const [keys, setKeys] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [isLoading, setIsLoading] = useState(true);
  const [isWeeklyData, setIsWeeklyData] = useState(false);

  const [showAllMembers, setShowAllMembers] = useState(false);

  const sortedKeys = useMemo(() => [...keys].sort(), [keys]);

  const handleDownloadCsv = useCallback(() => {
    if (data.length === 0) return;

    // Les en-têtes sont des noms d'utilisateurs : concaténées telles quelles, une virgule dans un
    // nom décalait toutes les colonnes, et un nom commençant par = + - @ partait en formule à
    // l'ouverture du tableur. csvEscapeCell, via buildCsvContent, quote et neutralise les deux.
    const headers = ['Date', ...sortedKeys, 'Total credits'];
    const rows = data.map((datum) => {
      const values = sortedKeys.map((key) => Number(datum[key] ?? 0));
      const dayTotal = values.reduce((total, value) => total + value, 0);
      return [datum.date, ...values, dayTotal];
    });

    const csvContent = buildCsvContent(headers, rows);
    // Le BOM aligne l'export sur celui du Portal : sans lui Excel lit les accents des noms en latin-1.
    const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const safeName = scopeName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const fromDate = startDate || 'start';
    const toDate = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `${csvPrefix}${safeName}_from_${fromDate}_to_${toDate}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  }, [csvPrefix, data, scopeName, sortedKeys, startDate]);

  const filledData = useMemo(() => {
    if (!startDate || (data.length === 0 && !isLoading)) return data;

    const result: ConsumptionData[] = [];
    const end = new Date();
    const current = new Date(startDate);
    const step = isWeeklyData ? 7 : 1;

    while (current <= end) {
      const date = current.toISOString().split('T')[0] as string;
      result.push(data.find((datum) => datum.date === date) || { date });
      current.setUTCDate(current.getUTCDate() + step);
    }
    return result;
  }, [data, isLoading, isWeeklyData, startDate]);

  const dailyTotals = useMemo(() => filledData.map((datum) => ({
    date: datum.date,
    credits: sortedKeys.reduce((sum, key) => sum + Number(datum[key] ?? 0), 0),
  })), [filledData, sortedKeys]);
  const memberTotals = useMemo(() => sortedKeys
    .map((key) => ({ name: key, credits: data.reduce((sum, datum) => sum + Number(datum[key] ?? 0), 0) }))
    .filter((member) => member.credits > 0)
    .sort((left, right) => right.credits - left.credits), [data, sortedKeys]);
  const periodTotal = useMemo(() => memberTotals.reduce((sum, member) => sum + member.credits, 0), [memberTotals]);
  const shownMembers = showAllMembers ? memberTotals : memberTotals.slice(0, MEMBERS_SHOWN);

  useEffect(() => {
    let cancelled = false;
    const fetchConsumption = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${endpoint}${endpoint.includes('?') ? '&' : '?'}days=${days}`);
        if (cancelled) return;
        setData(response.data.data);
        setKeys(response.data.keys);
        setStartDate(response.data.startDate);
        setIsWeeklyData(response.data.isWeekly);
      } catch (error) {
        if (!cancelled) console.error(fetchErrorLabel, error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void fetchConsumption();
    return () => {
      cancelled = true;
    };
  }, [days, endpoint, fetchErrorLabel]);

  const formatDate = (date: string) => (
    new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  );

  const tickEvery = Math.max(1, Math.ceil(dailyTotals.length / 6));
  const tickValues = dailyTotals.filter((_, index) => index % tickEvery === 0).map((datum) => datum.date);

  return (
    <section>
      <div className="flex items-center justify-between gap-4 pb-2">
        <h2 className="font-medium text-[#ECECEC]" style={{ fontSize: 17, lineHeight: '24px' }}>Credit usage</h2>
        <div className="flex items-center gap-1">
          <select
            value={days}
            onChange={(event) => setDays(Number.parseInt(event.target.value, 10))}
            className="h-9 cursor-pointer rounded-full bg-transparent px-3 text-[#ECECEC] outline-none transition-colors hover:bg-[#212121] focus-visible:bg-[#212121]"
            style={{ fontSize: 15 }}
            aria-label="Usage period"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={0}>All time</option>
          </select>
          <button
            type="button"
            onClick={handleDownloadCsv}
            disabled={isLoading || data.length === 0}
            className="flex h-9 w-9 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-[#212121] hover:text-white disabled:opacity-30"
            title="Download as CSV"
            aria-label="Download as CSV"
          >
            <Download size={18} strokeWidth={1.6} />
          </button>
        </div>
      </div>

      <div className="flex items-baseline gap-2 pt-1">
        <span className="tabular-nums text-[#ECECEC]" style={{ fontSize: 28, lineHeight: '36px' }}>{isLoading ? '–' : formatCreditsInteger(periodTotal)}</span>
        <span className="text-[#8E8E8E]" style={{ fontSize: 15 }}>credits {PERIOD_LABEL[days] ?? ''}</span>
      </div>

      <div className="mt-4 h-[220px] w-full">
        {isLoading ? (
          <div className="flex h-full items-center justify-center"><SimpleLoader size={24} /></div>
        ) : periodTotal > 0 ? (
          <ChartErrorBoundary label="Credit usage">
            <ResponsiveBar
              data={dailyTotals}
              keys={['credits']}
              indexBy="date"
              margin={{ top: 8, right: 0, bottom: 28, left: 48 }}
              padding={isWeeklyData ? 0.2 : 0.35}
              valueScale={{ type: 'linear' }}
              indexScale={{ type: 'band', round: true }}
              colors={BAR}
              borderRadius={2}
              theme={CHART_THEME}
              enableLabel={false}
              gridYValues={4}
              axisLeft={{ tickSize: 0, tickPadding: 12, tickValues: 4, format: (value) => formatCreditsInteger(Number(value)) }}
              axisBottom={{ tickSize: 0, tickPadding: 10, tickValues, format: formatDate }}
              onMouseEnter={(_datum, event) => { (event.currentTarget as SVGRectElement).style.fill = BAR_HOVER; }}
              onMouseLeave={(_datum, event) => { (event.currentTarget as SVGRectElement).style.fill = BAR; }}
              tooltip={({ value, indexValue }) => (
                <div className="rounded-[12px] border border-white/[0.06] bg-[#353535] px-3 py-2 shadow-2xl">
                  <div className="text-[#AFAFAF]" style={{ fontSize: 12 }}>{formatDate(String(indexValue))}</div>
                  <div className="tabular-nums text-[#ECECEC]" style={{ fontSize: 14 }}>{formatCreditsInteger(Number(value))} credits</div>
                </div>
              )}
              animate
              motionConfig="gentle"
              role="application"
            />
          </ChartErrorBoundary>
        ) : (
          <div className="flex h-full items-center justify-center text-[#8E8E8E]" style={{ fontSize: 15 }}>{emptyLabel}</div>
        )}
      </div>

      {!isLoading && memberTotals.length > 0 ? (
        <div className="mt-6">
          <h3 className="pb-1 text-[#8E8E8E]" style={{ fontSize: 14 }}>By member</h3>
          <div className="divide-y divide-white/[0.08]">
            {shownMembers.map((member) => (
              <div key={member.name} className="flex min-h-[48px] items-center gap-4 py-2">
                <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[#ECECEC]" style={{ fontSize: 15 }} title={member.name}>{member.name}</span>
                <span className="tabular-nums text-[#ECECEC]" style={{ fontSize: 15 }}>{formatCreditsInteger(member.credits)}</span>
                <span className="w-12 text-right tabular-nums text-[#8E8E8E]" style={{ fontSize: 14 }}>{Math.round((member.credits / periodTotal) * 100)}%</span>
              </div>
            ))}
          </div>
          {memberTotals.length > MEMBERS_SHOWN ? (
            <button
              type="button"
              onClick={() => setShowAllMembers((shown) => !shown)}
              className="mt-2 h-9 rounded-full px-3 text-[#AFAFAF] transition-colors hover:bg-[#212121] hover:text-white"
              style={{ fontSize: 14 }}
            >
              {showAllMembers ? 'Show fewer' : `Show all ${memberTotals.length}`}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default ConsumptionTimeline;
